import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatDateTime, ROLE_LABELS, QUOTATION_STATUSES, EQUIPMENT_STATUSES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, FileText, Wrench, ScrollText, CheckCircle2, Send, Clock, UserCog } from "lucide-react";

const CARD_DEFS = [
  { key: "customers", label: "Customers", icon: Users, to: "/customers", testid: "stat-customers" },
  { key: "equipment_in_lab", label: "Equipment In Lab", icon: Wrench, to: "/equipment", testid: "stat-equipment-in-lab" },
  { key: "equipment_total", label: "Equipment Total", icon: Wrench, to: "/equipment", testid: "stat-equipment-total" },
  { key: "quotations_draft", label: "Draft Quotations", icon: Clock, to: "/quotations", testid: "stat-quotations-draft" },
  { key: "quotations_sent", label: "Quotations Sent", icon: Send, to: "/quotations", testid: "stat-quotations-sent" },
  { key: "quotations_accepted", label: "Accepted", icon: CheckCircle2, to: "/quotations", testid: "stat-quotations-accepted" },
  { key: "quotations_total", label: "Total Quotations", icon: FileText, to: "/quotations", testid: "stat-quotations-total" },
  { key: "users", label: "System Users", icon: UserCog, to: "/users", testid: "stat-users" },
  { key: "audit_events", label: "Audit Events", icon: ScrollText, to: "/audit", testid: "stat-audit-events" },
];

const ROLE_CARDS = {
  admin: ["customers", "equipment_in_lab", "quotations_sent", "audit_events"],
  quality_manager: ["audit_events", "quotations_total", "equipment_in_lab", "customers"],
  technical_manager: ["equipment_in_lab", "equipment_total", "quotations_draft", "quotations_accepted"],
  supervisor: ["equipment_in_lab", "quotations_sent", "quotations_draft", "customers"],
  technician: ["equipment_in_lab", "equipment_total", "quotations_total", "customers"],
  customer: ["quotations_sent", "quotations_accepted", "equipment_in_lab", "quotations_total"],
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const cardKeys = ROLE_CARDS[user.role] || ROLE_CARDS.admin;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
          {user.role === "customer" ? "Customer Portal" : "Dashboard"}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Signed in as <span className="font-medium text-foreground">{user.name}</span> ·{" "}
          <span className="font-mono text-xs uppercase tracking-wider text-primary">{ROLE_LABELS[user.role]}</span>
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="dashboard-stats-grid">
        {cardKeys.map((key) => {
          const def = CARD_DEFS.find((c) => c.key === key);
          if (!def) return null;
          const value = stats?.counts?.[key];
          return (
            <Link key={key} to={def.to} data-testid={def.testid}>
              <Card className="h-full transition-colors hover:border-primary/50">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="font-mono text-[11px] font-medium uppercase tracking-widest text-muted-foreground">
                    {def.label}
                  </CardTitle>
                  <def.icon className="h-4 w-4 text-primary" />
                </CardHeader>
                <CardContent>
                  <div className="font-display text-3xl font-bold tracking-tight" data-testid={`${def.testid}-value`}>
                    {value ?? "—"}
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {stats?.recent_audit && (
          <Card data-testid="recent-audit-card">
            <CardHeader>
              <CardTitle className="text-base">Recent Audit Events</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {stats.recent_audit.length === 0 && <p className="text-sm text-muted-foreground">No audit events yet.</p>}
              {stats.recent_audit.map((log) => (
                <div key={log.id} className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <span className={`mr-2 font-mono text-[10px] font-semibold uppercase ${
                      log.action === "INSERT" ? "text-emerald-500" : log.action === "UPDATE" ? "text-amber-500" : "text-red-500"
                    }`}>{log.action}</span>
                    <span className="font-medium">{log.entity}</span>
                    <span className="ml-2 text-muted-foreground">by {log.user_name}</span>
                  </div>
                  <span className="shrink-0 font-mono text-[10px] text-muted-foreground">{formatDateTime(log.timestamp)}</span>
                </div>
              ))}
              {["admin", "quality_manager"].includes(user.role) && (
                <Link to="/audit" className="inline-block pt-1 text-xs font-medium text-primary hover:underline" data-testid="view-all-audit-link">
                  View full audit trail →
                </Link>
              )}
            </CardContent>
          </Card>
        )}

        {stats?.recent_quotations && (
          <Card data-testid="recent-quotations-card">
            <CardHeader>
              <CardTitle className="text-base">Recent Quotations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {stats.recent_quotations.length === 0 && <p className="text-sm text-muted-foreground">No quotations yet.</p>}
              {stats.recent_quotations.map((q) => (
                <Link key={q.id} to={`/quotations/${q.id}`} className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-sm transition-colors hover:border-primary/50" data-testid={`recent-quotation-${q.quotation_no}`}>
                  <span className="font-mono text-xs">{q.quotation_no}</span>
                  <StatusBadge status={q.status} label={QUOTATION_STATUSES[q.status]} />
                </Link>
              ))}
            </CardContent>
          </Card>
        )}

        <Card data-testid="recent-equipment-card">
          <CardHeader>
            <CardTitle className="text-base">Recent Equipment Inward</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {!stats && <p className="text-sm text-muted-foreground">Loading…</p>}
            {stats && stats.recent_equipment?.length === 0 && <p className="text-sm text-muted-foreground">No equipment registered yet.</p>}
            {stats?.recent_equipment?.map((e) => (
              <Link key={e.id} to="/equipment" className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-sm transition-colors hover:border-primary/50" data-testid={`recent-equipment-${e.work_order_no}`}>
                <div className="min-w-0">
                  <span className="font-mono text-xs text-primary">{e.work_order_no}</span>
                  <span className="ml-2 truncate">{e.name}</span>
                </div>
                <StatusBadge status={e.status} label={EQUIPMENT_STATUSES[e.status]} />
              </Link>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
